import { invokePublicSmartAuth } from "./invokePublicSmartAuth";
import { invokePublicSendPhoneOtp } from "./invokePublicSendPhoneOtp";
import { sendPasswordRecoveryEmail } from "../utils/sendPasswordRecoveryEmail";

/** GoTrue blocks anon/SMS OTP when sign-ups are disabled or phone is in a locked state. */
export function isOtpDisabledAuthError(e: unknown): boolean {
    if (!(e instanceof Error)) return false;
    const code = typeof (e as { code?: string }).code === "string" ? (e as { code: string }).code : "";
    const msg = e.message.toLowerCase();
    return code === "otp_disabled" || msg.includes("signups not allowed") || msg.includes("signup not allowed");
}

/** Phone tab on login: SMS OTP only (Edge). No silent email fallback when the user asked for a text. */
export async function requestLoginPhoneSmsOtp(phone: string): Promise<void> {
    await invokePublicSendPhoneOtp(phone);
}

export type ConfirmAccountPhoneResult = { channel: "sms" } | { channel: "email_recovery"; email: string };

/** Confirm-account phone path: SMS OTP; if blocked, same recovery email flow as the email tab. */
export async function requestConfirmAccountPhoneOrRecoveryEmail(args: {
    phone: string;
    inviteEmail?: string | null;
}): Promise<ConfirmAccountPhoneResult> {
    const { phone, inviteEmail } = args;
    try {
        await invokePublicSendPhoneOtp(phone);
        return { channel: "sms" };
    } catch (e) {
        const em = inviteEmail?.trim();
        if (em && isOtpDisabledAuthError(e)) {
            await sendPasswordRecoveryEmail(em);
            return { channel: "email_recovery", email: em };
        }
        throw e;
    }
}

export type ForgotPasswordPhoneResult = { channel: "sms" } | { channel: "email_recovery"; email: string };

/** Forgot password on phone: SMS OTP; if blocked, look up account email and send recovery email. */
export async function requestForgotPasswordSmsOrRecoveryEmail(phone: string): Promise<ForgotPasswordPhoneResult> {
    try {
        await invokePublicSendPhoneOtp(phone);
        return { channel: "sms" };
    } catch (e) {
        if (!isOtpDisabledAuthError(e)) throw e;
        const smart = await invokePublicSmartAuth({ phone });
        const em = smart.email?.trim();
        if (!em || smart.auth_method === "signup") throw e;
        await sendPasswordRecoveryEmail(em);
        return { channel: "email_recovery", email: em };
    }
}
